// PRESTOplayPlayerAdapter.js
'use strict';

import {
  getWidevineRobustnessCustomData,
  loadPrestoplayScript,
  setWidevineServerCertificate,
} from '../resource/loadPrestoplayScript.js';

import BasePlayerAdapter from '../resource/BasePlayerAdapter.js';
import { PlayerAdapterEvent } from '../resource/playerAdapterConstants.js';
import { NULL_FUNCTION } from '../resource/defaults.js';
import {
  COM_APPLE_FPS,
  COM_WIDEVINE_ALPHA,
  PRESTOPLAY_DRM_ENV_NAME,
} from '../resource/drmConstants.js';
import { getHeader } from '../resource/fetchOptionHeader.js';
import { getWidevineServerCertificateBuffer } from '../resource/formatVodDrmSource.js';
import {
  DEFAULT_HLS_CODECS,
  formatSource,
  getPlaybackTuning,
} from '../resource/playerUtils.js';

// A seek lands on an unbuffered position, so whichever tier ABR currently
// favours has to arrive in full before a single frame can be shown — on sd that
// is a ~1.8MB wait after every drag. The ladder is pinned to its lowest rung for
// the length of the seek instead, and handed back to ABR once seeking settles;
// this is the backstop for when `seeked` never arrives, so it can never stay
// pinned.
const SEEK_RENDITION_LOCK_TIMEOUT_MS = 5000;
// A drag fires a `seek()` per pointer move and a `seeked` for each one that
// completes, so releasing on the first `seeked` would leave the rest of the drag
// unpinned. The release waits out this much quiet instead.
const SEEK_RENDITION_LOCK_RELEASE_DELAY_MS = 400;

// clpp's own default, written out so the short and the long-form read as the
// same field with a different value. Note this is not what stops a pooled player
// reusing a short's 500 kbps seed: unlike shaka's `configure()`, clpp merges each
// `open()` config into fresh defaults rather than into the previous call's.
const CLPP_DEFAULT_BANDWIDTH_ESTIMATE = 1000;
// A ceiling only the lowest ladder tier (ld, 616 kbps) fits under. What a pooled
// player does carry over is the bandwidth estimator itself — it outlives
// `stop()`, so the previous playback's measurement, not the seed, decides the
// first tier. `resetAbr()` cannot stand in for this ceiling either: called before
// `open()` it re-picks a track for the outgoing content, called after it the
// first choice has already happened. `initialRestrictions` covers exactly that
// first selection, so ABR still climbs from there.
const LOWEST_RENDITION_ONLY_MAX_BANDWIDTH = 700000;

class PRESTOplayPlayerAdapter extends BasePlayerAdapter {
  static async ensureReady() {
    await loadPrestoplayScript();
  }

  constructor(options = {}) {
    super(options);

    this._prestoPlayer = null;
    this._eventListeners = new Map();
    this._playerOptions = options.playerOptions || {};

    this._getToken = options.getToken || NULL_FUNCTION;
    this._getClientId = options.getClientId || NULL_FUNCTION;

    this._container = null;
    this._prestoEventHandlers = [];
    this._boundVolumeChange = null;
    this._isSeekRenditionLocked = false;
    this._seekRenditionLockTimerId = null;
    // Set per source in load(); the pool builds one adapter and reuses it for
    // whatever feature comes next, so this cannot come from the constructor.
    this._drmFeatureName = '';
  }

  /**
   * Pin the video ladder to its lowest rendition so the segment a seek has to
   * fetch is the smallest one available. No-op when the platform cannot select
   * tracks or the asset has a single rendition.
   */
  _lockLowestRenditionForSeek() {
    const trackManager = this._prestoPlayer?.getTrackManager?.();
    if (!trackManager?.canSelectVideoTracks?.()) return;

    const renditions = (trackManager.getVideoTracks?.() || []).flatMap(
      track => track.renditions || []
    );
    if (renditions.length < 2) return;

    if (!this._isSeekRenditionLocked) {
      const lowestRendition = renditions.reduce((lowest, rendition) =>
        (rendition.bandwidth ?? Infinity) < (lowest.bandwidth ?? Infinity)
          ? rendition
          : lowest
      );
      // Playback opens on the lowest tier already (the ABR bandwidth seed), so
      // the first seek would otherwise switch that tier to itself — enough for
      // the player to rebuild its buffer and fetch the segment it is sitting on
      // a second time. Bail out entirely rather than lock: with nothing to pin,
      // the release below would hand a rendition back that was never taken.
      // `getLoadingVideoRendition` first — a switch that has been asked for but
      // not completed is the one that decides where playback ends up.
      const currentRendition =
        trackManager.getLoadingVideoRendition?.() ??
        trackManager.getVideoRendition?.();
      if (currentRendition?.id === lowestRendition.id) return;

      // `clearBuffer` is left at its default of false on purpose: seeking into
      // video that is already buffered should keep the quality it was buffered
      // at rather than throw it away and fetch the same range again.
      trackManager.setVideoRendition(lowestRendition);
      this._isSeekRenditionLocked = true;
    }

    this._armSeekRenditionLockBackstop();
  }

  /** Restart the backstop that keeps the ladder from staying pinned. */
  _armSeekRenditionLockBackstop() {
    clearTimeout(this._seekRenditionLockTimerId);
    this._seekRenditionLockTimerId = setTimeout(
      () => this._releaseSeekRenditionLock(),
      SEEK_RENDITION_LOCK_TIMEOUT_MS
    );
  }

  /** Hand tier selection back to ABR once seeking has stayed quiet. */
  _scheduleSeekRenditionLockRelease() {
    if (!this._isSeekRenditionLocked) return;

    clearTimeout(this._seekRenditionLockTimerId);
    this._seekRenditionLockTimerId = setTimeout(() => {
      // A `seeked` can belong to an earlier seek than the one still in flight —
      // a drag emits one per pointer move, and they arrive out of step with the
      // seeks that produced them. Releasing on that one would hand the ladder
      // back before the seek actually running has a frame to show.
      if (this._videoElement?.seeking) {
        this._armSeekRenditionLockBackstop();
        return;
      }
      this._releaseSeekRenditionLock();
    }, SEEK_RENDITION_LOCK_RELEASE_DELAY_MS);
  }

  /** Whether the video at `time` is buffered, so no new segment is needed. */
  _getIsTimeBuffered(time) {
    const buffered = this._videoElement?.buffered;
    if (!buffered?.length) return false;

    for (let index = 0; index < buffered.length; index += 1) {
      if (time >= buffered.start(index) && time <= buffered.end(index)) {
        return true;
      }
    }
    return false;
  }

  /** Hand tier selection back to ABR after a seek. */
  _releaseSeekRenditionLock() {
    clearTimeout(this._seekRenditionLockTimerId);
    this._seekRenditionLockTimerId = null;
    if (!this._isSeekRenditionLocked) return;

    this._isSeekRenditionLocked = false;
    this._prestoPlayer?.getTrackManager?.()?.setVideoRendition(null);
  }

  _setupAllEvents() {
    const player = this._prestoPlayer;

    const { events, Player } = this._clpp;

    // STATE_CHANGED → PLAYING, PAUSED, ENDED
    this._onPrestoEvent(player, events.STATE_CHANGED, event => {
      const currentState = event.detail?.currentState;
      switch (currentState) {
        case Player.State.PLAYING:
          this._emit(PlayerAdapterEvent.PLAYING, event.detail);
          break;
        case Player.State.PAUSED:
          this._emit(PlayerAdapterEvent.PAUSED, event.detail);
          break;
        case Player.State.ENDED:
          this._emit(PlayerAdapterEvent.ENDED, event.detail);
          break;
        default:
          break;
      }
    });

    this._onPrestoEvent(player, events.ERROR, event => {
      this._emit(PlayerAdapterEvent.ERROR, event.detail || event);
    });

    this._onPrestoEvent(player, events.LOADEDMETADATA, () => {
      this._emit(PlayerAdapterEvent.READY);
    });

    this._onPrestoEvent(player, events.SEEKING, () => {
      this._emit(PlayerAdapterEvent.SEEK);
    });

    this._onPrestoEvent(player, events.SEEKED, () => {
      this._scheduleSeekRenditionLockRelease();
      this._emit(PlayerAdapterEvent.SEEKED);
    });

    this._onPrestoEvent(player, events.BUFFERING_STARTED, event => {
      this._emit(PlayerAdapterEvent.STALL_STARTED, event.detail || {});
    });

    this._onPrestoEvent(player, events.BUFFERING_ENDED, event => {
      this._emit(PlayerAdapterEvent.STALL_ENDED, event.detail || {});
    });

    // PRESTO proxies HTML5 events — listen for timeupdate directly.
    this._onPrestoEvent(player, 'timeupdate', () => {
      this._emit(PlayerAdapterEvent.TIME_CHANGED, player.getPosition());
    });

    // PRESTO has no native MUTED/UNMUTED events — listen to video element.
    this._boundVolumeChange = () => {
      this._emit(PlayerAdapterEvent.VOLUME_CHANGED, {
        volume: this._videoElement?.volume ?? 1,
      });
    };
    this._videoElement?.addEventListener(
      'volumechange',
      this._boundVolumeChange
    );
  }

  _onPrestoEvent(player, eventName, handler) {
    player.on(eventName, handler);
    this._prestoEventHandlers.push({ eventName, handler });
  }

  _removeAllPrestoEvents() {
    if (this._prestoPlayer) {
      for (const { eventName, handler } of this._prestoEventHandlers) {
        try {
          this._prestoPlayer.off(eventName, handler);
        } catch {
          // Player may already be destroyed.
        }
      }
    }
    this._prestoEventHandlers = [];

    if (this._boundVolumeChange && this._videoElement) {
      this._videoElement.removeEventListener(
        'volumechange',
        this._boundVolumeChange
      );
    }
    this._boundVolumeChange = null;
  }

  _emit(event, data) {
    const listeners = this._eventListeners.get(event);
    if (listeners) {
      for (const callback of listeners) {
        try {
          callback(data);
        } catch (error) {
          this._logError('_emit', error);
        }
      }
    }
  }

  async initialize(videoElement) {
    this._guardDestroyed();

    if (this._prestoPlayer) {
      this._log('initialize', 'already initialized, skipping');
      return;
    }

    this._clpp = await loadPrestoplayScript();

    this._videoElement = videoElement;

    // Wrap video element in a container to prevent PRESTO auto-wrapping DOM moves.
    this._container = document.createElement('div');
    if (videoElement.parentNode) {
      videoElement.parentNode.insertBefore(this._container, videoElement);
    }
    this._container.appendChild(videoElement);

    const playerConfig = this._playerOptions.license
      ? {
          license: this._playerOptions.license,
          viewerId: getHeader('X-Client-ID') || 'anonymous',
        }
      : {};

    this._prestoPlayer = new this._clpp.Player(videoElement, playerConfig, {
      containerEl: this._container,
    });

    this._setupAllEvents();
  }

  async load(sourceConfig) {
    this._guardDestroyed();

    if (!this._prestoPlayer) {
      throw new Error('Player not created');
    }

    // A re-load without unload() (license reload, reload()) leaves the lock
    // pointing at a pipeline that no longer exists.
    this._releaseSeekRenditionLock();

    this._drmFeatureName = sourceConfig.drmFeatureName || '';

    if (sourceConfig.drm) {
      await this._loadWithDrm(sourceConfig);
      return;
    }

    await this._loadWithoutDrm(sourceConfig);
  }

  async _loadWithDrm(sourceConfig) {
    const { src, drm } = sourceConfig;
    const { dash, hls } = src || {};
    const { keySystem, robustness, widevineServerCertificateBase64 } = drm;

    const isFairPlay = keySystem === COM_APPLE_FPS;
    const manifestUrl = isFairPlay ? hls : dash;

    const authToken = drm.drmToken || this._getToken() || '';
    const clientId = this._getClientId() || getHeader('X-Client-ID') || '';

    if (widevineServerCertificateBase64) {
      setWidevineServerCertificate({
        serverCertificate: getWidevineServerCertificateBuffer({
          widevineServerCertificateBase64,
        }),
      });
    }

    const widevineRobustnessCustomData =
      keySystem === COM_WIDEVINE_ALPHA && robustness
        ? getWidevineRobustnessCustomData({
            // Use lowest capability — due to DRM detection only tests video capability.
            audioRobustness: ['SW_SECURE_CRYPTO'],
            videoRobustness: [robustness],
          })
        : {};

    const source = isFairPlay
      ? { url: manifestUrl, type: this._clpp.Type.HLS, drmProtected: true }
      : { url: manifestUrl, type: this._clpp.Type.DASH, drmProtected: true };

    const openConfig = {
      source,
      drm: {
        env: PRESTOPLAY_DRM_ENV_NAME,
        preferredDrmSystem: keySystem,
        customData: {
          token: authToken,
          clientId,
          drmToken: drm.drmToken,
          ...widevineRobustnessCustomData,
        },
      },
    };

    await this._executeOpen(openConfig);
  }

  async _loadWithoutDrm(sourceConfig) {
    const { src } = sourceConfig;
    const [primarySource] = formatSource(src || {});

    const source =
      primarySource?.typeKey === 'dash'
        ? { url: primarySource.src, type: this._clpp.Type.DASH }
        : primarySource?.typeKey === 'hls'
          ? { url: primarySource.src, type: this._clpp.Type.HLS }
          : primarySource?.src || '';

    await this._executeOpen({ source, hlsDefaultCodecs: src?.hlsCodecs });
  }

  async _executeOpen(openConfig) {
    try {
      const { hlsDefaultCodecs, ...prestoOpenConfig } = openConfig;

      // iOS autoplay requires muted. Set before open() to satisfy autoplay policy.
      this._prestoPlayer.setMuted(true);
      const playbackTuning = getPlaybackTuning({
        drmFeatureName: this._drmFeatureName,
      });
      await this._prestoPlayer.open({
        ...prestoOpenConfig,
        abr: {
          defaultBandwidthEstimate: CLPP_DEFAULT_BANDWIDTH_ESTIMATE,
          ...playbackTuning.abrBandwidthSeed,
          // Only defined for the short. clpp's first track choice reads
          // `initialRestrictions` *instead of* `abr.restrictions`, not on top of
          // it, so writing an unrestricted ceiling here would replace the
          // engine's own defaults — including the 360p cap it applies when a
          // mobile browser asks for Data Saver.
          ...(playbackTuning.shouldOpenOnLowestRendition && {
            initialRestrictions: {
              maxBandwidth: LOWEST_RENDITION_ONLY_MAX_BANDWIDTH,
            },
          }),
        },
        streaming: {
          rebufferingGoal: playbackTuning.rebufferingGoalSeconds,
          // Known corner for the long-form video that keeps this on: during the
          // ≤5s seek rendition lock it is pinned to the lowest tier too, so on a
          // pathologically slow network its aborts have nowhere to drop to
          // either, until the lock's backstop releases it.
          enableStalledSegmentAbort: playbackTuning.shouldAbortStalledSegment,
        },
        manifest: {
          // Standalone media playlists lack CODECS; use the master CODECS the
          // AES rewrite preserved, otherwise keep the legacy fallback.
          hlsDefaultCodecs: hlsDefaultCodecs || DEFAULT_HLS_CODECS,
        },
      });
      // PRESTOplay open() rebuilds the media pipeline and resets muted state; re-apply.
      this._prestoPlayer.setMuted(true);
    } catch (error) {
      this._logError('load', error);
      throw error;
    }
  }

  async unload() {
    this._guardDestroyed();
    this._releaseSeekRenditionLock();

    if (this._prestoPlayer) {
      try {
        await this._prestoPlayer.release();
      } catch (error) {
        this._logError('unload', error);
      }
    }
  }

  async destroy() {
    if (this._isDestroyed) return;

    this._isDestroyed = true;
    this._releaseSeekRenditionLock();
    this._removeAllPrestoEvents();

    if (this._prestoPlayer) {
      try {
        await this._prestoPlayer.destroy();
      } catch (error) {
        this._logError('destroy', error);
      }
      this._prestoPlayer = null;
    }

    // Restore video element to its original position before removing container.
    if (this._container && this._videoElement) {
      if (this._container.parentNode) {
        this._container.parentNode.insertBefore(
          this._videoElement,
          this._container
        );
      }
      this._container.remove();
    }

    this._videoElement = null;
    this._container = null;
    this._eventListeners.clear();
  }

  async play() {
    this._guardDestroyed();
    return this._prestoPlayer?.play();
  }

  pause() {
    if (this._isDestroyed) return;
    // ponytail: pause() returns a Promise that rejects if player state disallows it (e.g. after failed load)
    this._prestoPlayer?.pause()?.catch(() => {});
  }

  mute() {
    this._guardDestroyed();
    this._prestoPlayer?.setMuted(true);
    this._emit(PlayerAdapterEvent.MUTED);
  }

  unmute() {
    this._guardDestroyed();
    this._prestoPlayer?.setMuted(false);
    this._emit(PlayerAdapterEvent.UNMUTED);
  }

  isMuted() {
    this._guardDestroyed();
    return this._prestoPlayer?.isMuted() ?? true;
  }

  seek(time) {
    this._guardDestroyed();
    // A looping short restarts through `seek(0)` and a license reload restores
    // its position the same way, so this is not only a user dragging into video
    // nobody has downloaded yet. Dropping a tier for a segment already buffered
    // would cost quality and buy nothing.
    // Once the lock is held, a buffered target still goes through the lock path:
    // a drag passing over buffered video has to push the backstop out, or a
    // release scheduled earlier lands while the drag is still going.
    if (this._isSeekRenditionLocked || !this._getIsTimeBuffered(time)) {
      this._lockLowestRenditionForSeek();
    }
    this._prestoPlayer?.setPosition(time);
  }

  getCurrentTime() {
    this._guardDestroyed();
    return this._prestoPlayer?.getPosition() ?? 0;
  }

  getDuration() {
    this._guardDestroyed();
    return this._prestoPlayer?.getDuration() ?? 0;
  }

  setPlaybackRate(rate) {
    this._guardDestroyed();
    this._prestoPlayer?.setRate(rate);
  }

  getPlaybackRate() {
    this._guardDestroyed();
    return this._prestoPlayer?.getRate() ?? 1;
  }

  on(event, callback) {
    this._guardDestroyed();

    if (!this._eventListeners.has(event)) {
      this._eventListeners.set(event, new Set());
    }
    this._eventListeners.get(event).add(callback);
  }

  off(event, callback) {
    if (this._isDestroyed) return;
    this._eventListeners.get(event)?.delete(callback);
  }

  getInternalPlayer() {
    return this._prestoPlayer;
  }

  getContainer() {
    return this._container || null;
  }
}

export default PRESTOplayPlayerAdapter;
